// packages/web/src/components/admin/user-create-sheet.tsx
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useInviteUser } from "@/api/users/mutations";
import { InviteLinkDialog } from "./invite-link-dialog.tsx";
import type { Role } from "@/lib/plan-user-save";

export interface UserCreateSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FieldErrors {
  email?: string;
  name?: string;
}

export function UserCreateSheet({ open, onOpenChange }: UserCreateSheetProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<Role>("worker");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [dialog, setDialog] = useState<null | {
    email: string;
    url: string;
    emailDeliveryFailed: boolean;
  }>(null);

  const inviteMut = useInviteUser();

  function validate(values: { email: string; name: string }): FieldErrors {
    const next: FieldErrors = {};
    if (!values.email.trim()) next.email = t("admin.users.create-sheet.errors.email-required");
    if (!values.name.trim()) next.name = t("admin.users.create-sheet.errors.name-required");
    return next;
  }

  async function onSubmit() {
    const next = validate({ email, name });
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    try {
      const result = await inviteMut.mutateAsync({ body: { email, name, role } });
      toast.success(t("admin.users.create-sheet.toast.invite-sent", { email }));
      setDialog({
        email,
        url: result.invite.url,
        emailDeliveryFailed: result.invite.emailDeliveryFailed,
      });
      onOpenChange(false);
      setEmail("");
      setName("");
      setRole("worker");
      setErrors({});
    } catch (e) {
      const message =
        typeof e === "object" && e && "message" in e
          ? String((e as { message: unknown }).message)
          : "";
      if (message.includes("Email already in use")) {
        setErrors({ email: t("admin.users.create-sheet.errors.email-in-use") });
      } else {
        toast.error(
          t("admin.users.create-sheet.errors.invite-failed", {
            message: message || t("admin.users.create-sheet.errors.unknown"),
          }),
        );
      }
    }
  }

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange} direction="right">
        <DrawerContent className="ml-auto h-full w-full max-w-md bg-surface">
          <DrawerHeader>
            <DrawerTitle>{t("admin.users.create-sheet.title")}</DrawerTitle>
          </DrawerHeader>
          <div className="space-y-4 px-4 pb-4">
            <div className="space-y-1.5">
              <Label htmlFor="ucs-email">{t("admin.users.create-sheet.email.label")}</Label>
              <Input
                id="ucs-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={!!errors.email}
              />
              {errors.email ? <p className="text-[12px] text-vroom-red">{errors.email}</p> : null}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ucs-name">{t("admin.users.create-sheet.name.label")}</Label>
              <Input
                id="ucs-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                aria-invalid={!!errors.name}
              />
              {errors.name ? <p className="text-[12px] text-vroom-red">{errors.name}</p> : null}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ucs-role">{t("admin.users.create-sheet.role.label")}</Label>
              <select
                id="ucs-role"
                className="h-9 w-full rounded-3 border border-line bg-surface px-2 text-[13px]"
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
              >
                <option value="worker">{t("common.roles.worker.label")}</option>
                <option value="approver">{t("common.roles.approver.label")}</option>
                <option value="super_admin">{t("common.roles.super-admin.label")}</option>
              </select>
            </div>
            <p className="text-[12px] text-ink-500">{t("admin.users.create-sheet.invite-info")}</p>
          </div>
          <DrawerFooter className="flex-row justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={inviteMut.isPending}
            >
              {t("common.buttons.cancel.label")}
            </Button>
            <Button variant="primary" onClick={onSubmit} disabled={inviteMut.isPending}>
              {t("admin.users.create-sheet.submit.label")}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      {dialog ? (
        <InviteLinkDialog
          open
          onOpenChange={(v) => !v && setDialog(null)}
          email={dialog.email}
          url={dialog.url}
          emailDeliveryFailed={dialog.emailDeliveryFailed}
        />
      ) : null}
    </>
  );
}
